import { CircularProgress, styled } from '@mui/material'
import { VersionReportVersionEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import moment from 'moment'
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded'
import {
  LoadingContainer,
  TableContainer,
  TableHeader,
  TableBody,
  TableRow,
  HeaderCell,
  DataCell,
  NameCell,
  NameText,
  ReportRowIcon,
  DialogContent,
  SelectMenuHeader,
  BreadcrumbListIcon,
  BreadcrumbFolderIcon,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  AddButton,
} from './commons'
import { StyledDivider } from '../commons'
import { RenameMenu } from './RenameMenu'
import { memo, useCallback } from 'react'

interface ReportMenuProps {
  templateName: string
  reports: VersionReportVersionEntity[]
  loading: boolean
  onReportSelect: (report: VersionReportVersionEntity) => void
  onReportUpdate: (report: VersionReportVersionEntity) => void
  onBackToTemplate: () => void
  onCreateNewReport: () => void
  onClose: () => void
}

export const ReportMenu = ({
  templateName,
  reports,
  loading,
  onReportSelect,
  onReportUpdate,
  onBackToTemplate,
  onCreateNewReport,
  onClose,
}: ReportMenuProps) => {
  return (
    <>
      <SelectMenuHeader
        breadcrumb={
          <>
            <BreadcrumbListIcon />
            <BreadcrumbLink onClick={onBackToTemplate}>
              フォルダ一覧
            </BreadcrumbLink>
            <BreadcrumbSeparator />
            <BreadcrumbFolderIcon />
            <BreadcrumbCurrent>{templateName}</BreadcrumbCurrent>
          </>
        }
        actions={
          <AddButton
            onClick={onCreateNewReport}
            tooltip="報告書を作成"
            icon={<NoteAddRoundedIcon sx={{ fontSize: 18 }} />}
          />
        }
        onClose={onClose}
      />
      <StyledDivider />
      <DialogContent>
        {loading ? (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        ) : (
          <TableContainer>
            <TableHeader>
              <HeaderCell width="52%">名称</HeaderCell>
              <HeaderCell width="22%">更新者</HeaderCell>
              <HeaderCell width="20%">更新日時</HeaderCell>
              <HeaderCell width="6%"></HeaderCell>
            </TableHeader>
            <TableBody>
              {reports.map(report => (
                <ReportRow
                  key={report.uuid}
                  report={report}
                  onReportSelect={onReportSelect}
                  onReportUpdate={onReportUpdate}
                />
              ))}
            </TableBody>
          </TableContainer>
        )}
      </DialogContent>
    </>
  )
}

const ReportRow = memo(
  ({
    report,
    onReportSelect,
    onReportUpdate,
  }: {
    report: VersionReportVersionEntity
    onReportSelect: (report: VersionReportVersionEntity) => void
    onReportUpdate: (report: VersionReportVersionEntity) => void
  }) => {
    const handleSelect = useCallback(() => {
      onReportSelect(report)
    }, [report, onReportSelect])

    const handleRename = useCallback(() => {
      onReportUpdate(report)
    }, [report, onReportUpdate])

    return (
      <ReportTableRow>
        <NameCell width="52%" onClick={handleSelect}>
          <ReportRowIcon />
          <NameText>{report.name}</NameText>
        </NameCell>
        <DataCell width="22%" onClick={handleSelect}>
          {report.updatedBy.name}
        </DataCell>
        <DataCell width="20%" onClick={handleSelect}>
          {moment(report.updatedAt).format('MM/DD HH:mm')}
        </DataCell>
        <ActionCell width="6%">
          <MenuButtonWrapper className="report-action-menu">
            <RenameMenu onRename={handleRename} label="報告書名を編集する" />
          </MenuButtonWrapper>
        </ActionCell>
      </ReportTableRow>
    )
  }
)

ReportRow.displayName = 'ReportRow'

const ReportTableRow = styled(TableRow)({
  '&:hover .report-action-menu': {
    opacity: 1,
  },
})

const ActionCell = styled('div')<{ width: string }>(({ width }) => ({
  width,
  padding: '12px 8px',
  fontSize: '13px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}))

const MenuButtonWrapper = styled('div')({
  opacity: 0,
  transition: 'opacity 0.2s',
})
